/** @jsxImportSource @emotion/react */
import React from "react";
import { Col, Form, Modal, Row, Button, Checkbox, Divider } from "antd";
import { useForm, Controller } from "react-hook-form";
import * as Styles from "./styles";
import { HomeColumns } from "../../../usecases/Home/usecase.d";

interface ModalHomeSettingColumnsProps {
  onCancel: () => void;
  onSubmit: (columns: HomeColumns) => void;
  visible: boolean;
  columns: HomeColumns;
}

const ModalHomeSettingColumnsComponent: React.FC<ModalHomeSettingColumnsProps> =
  ({ visible, onCancel, onSubmit, columns }: ModalHomeSettingColumnsProps) => {
    const { handleSubmit, control, setValue } = useForm<HomeColumns>({
      mode: "onSubmit",
      defaultValues: columns,
    });

    const handleSubmitHandle = React.useCallback(
      async (data: HomeColumns) => {
        onSubmit(data);
      },
      [onSubmit],
    );

    const onCancelHandle = React.useCallback(() => {
      onCancel();
    }, [onCancel]);

    const setCheckboxes = React.useCallback(
      (checked: boolean) => {
        setValue("homeName", checked, { shouldValidate: true });
        setValue("homeRange", checked, { shouldValidate: true });
        setValue("homeStorey", checked, { shouldValidate: true });
        setValue("homeBedNo", checked, { shouldValidate: true });
        setValue("homeBathNo", checked, { shouldValidate: true });
        setValue("homeGarageNo", checked, { shouldValidate: true });
        setValue("homeFromPrice", checked, { shouldValidate: true });
        setValue("homeToPrice", checked, { shouldValidate: true });
        setValue("homeLotSize", checked, { shouldValidate: true });
        setValue("homeFloorplanMapUrl", checked, { shouldValidate: true });
      },
      [setValue],
    );

    const onSelectAllClickHandle = React.useCallback(() => {
      setCheckboxes(true);
    }, [setCheckboxes]);

    const onClearAllClickHandle = React.useCallback(() => {
      setCheckboxes(false);
    }, [setCheckboxes]);

    return (
      <Modal
        title="Filter"
        visible={visible}
        closable={false}
        footer={[
          <Button key="back" size="large" onClick={onCancelHandle}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            size="large"
            onClick={handleSubmit(handleSubmitHandle)}
          >
            OK
          </Button>,
        ]}
      >
        <Form layout="vertical" onFinish={handleSubmit(handleSubmitHandle)}>
          <Row css={Styles.colGutterStyle}>
            <Button type="link" onClick={onSelectAllClickHandle}>
              Select All
            </Button>
            <Divider type="vertical" css={Styles.verticalDivider} />
            <Button type="link" onClick={onClearAllClickHandle}>
              Clear All
            </Button>
          </Row>
          <Row gutter={16}>
            <Col xs={12} css={Styles.colGutterStyle}>
              <Controller
                name="homeFloorplanMapUrl"
                control={control}
                render={({ field }) => (
                  <Checkbox {...field} checked={field.value}>
                    Floorplan Map
                  </Checkbox>
                )}
              />
            </Col>
            <Col xs={12} css={Styles.colGutterStyle}>
              <Controller
                name="homeName"
                control={control}
                render={({ field }) => (
                  <Checkbox {...field} checked={field.value}>
                    Name
                  </Checkbox>
                )}
              />
            </Col>
            <Col xs={12} css={Styles.colGutterStyle}>
              <Controller
                name="homeBedNo"
                control={control}
                render={({ field }) => (
                  <Checkbox {...field} checked={field.value}>
                    Number of Bed
                  </Checkbox>
                )}
              />
            </Col>
            <Col xs={12} css={Styles.colGutterStyle}>
              <Controller
                name="homeRange"
                control={control}
                render={({ field }) => (
                  <Checkbox {...field} checked={field.value}>
                    Range
                  </Checkbox>
                )}
              />
            </Col>
            <Col xs={12} css={Styles.colGutterStyle}>
              <Controller
                name="homeBathNo"
                control={control}
                render={({ field }) => (
                  <Checkbox {...field} checked={field.value}>
                    Number of Bath
                  </Checkbox>
                )}
              />
            </Col>
            <Col xs={12} css={Styles.colGutterStyle}>
              <Controller
                name="homeFromPrice"
                control={control}
                render={({ field }) => (
                  <Checkbox {...field} checked={field.value}>
                    From Price
                  </Checkbox>
                )}
              />
            </Col>
            <Col xs={12} css={Styles.colGutterStyle}>
              <Controller
                name="homeGarageNo"
                control={control}
                render={({ field }) => (
                  <Checkbox {...field} checked={field.value}>
                    Number of Garage
                  </Checkbox>
                )}
              />
            </Col>
            <Col xs={12} css={Styles.colGutterStyle}>
              <Controller
                name="homeToPrice"
                control={control}
                render={({ field }) => (
                  <Checkbox {...field} checked={field.value}>
                    To Price
                  </Checkbox>
                )}
              />
            </Col>
            <Col xs={12} css={Styles.colGutterStyle}>
              <Controller
                name="homeLotSize"
                control={control}
                render={({ field }) => (
                  <Checkbox {...field} checked={field.value}>
                    Lot Size
                  </Checkbox>
                )}
              />
            </Col>
            <Col xs={12} css={Styles.colGutterStyle}>
              <Controller
                name="homeStorey"
                control={control}
                render={({ field }) => (
                  <Checkbox {...field} checked={field.value}>
                    Storey
                  </Checkbox>
                )}
              />
            </Col>
          </Row>
        </Form>
      </Modal>
    );
  };

export const ModalHomeSettingColumns = React.memo(
  ModalHomeSettingColumnsComponent,
);
